import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, InfinityIcon, GiftIcon } from 'lucide-react';
import { QuantityStepper, money } from './QuantityStepper';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { offers, Offer } from '../data/product';
import { cartLink, variantIdOf, PRODUCT_LINKS } from '../data/productLinks';
import { Link } from 'react-router-dom';
import { CompanyPack } from './CompanyPack';
import { IncludedGear } from './IncludedGear';
import { UpsellModal } from './UpsellModal';
import { LifetimeUpgradeOption } from './LifetimeUpgradeOption';
import { useUpsellFlow } from '../hooks/useUpsellFlow';
import { buildFlow, checkoutFlow, suggestionFor, PackSuggestion, LIFETIME_UPGRADE_PRICE, SINGLE_PRICE, SINGLE_WITH_LIFETIME_PRICE } from '../data/upsellFlows';
import { onCarryToSingle, requestBusinessQuantity, scrollToPackCta } from '../utils/offerBus';
import { flowSeen, setSelectionSource, SelectionSource } from '../utils/funnelState';
import { PaymentLogos } from './PaymentLogos';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { trackCustom, packEvents, trackShopifyAddToCart } from '../utils/tracking';
import { PRODUCT_NAME, warrantyCopy, pricing } from '../data/promotion';
import { useActiveSale } from '../hooks/useActiveSale';
import { RxQuantityControl } from './RxQuantityControl';
import { CareKitControl } from './CareKitControl';
import { setPendingCare, setPendingRx } from '../utils/cartAddons';
import { builderPrices } from '../data/builder';
import { withPrice } from '../utils/ctaPrice';
import { ReviewsStrip } from './BannerOverlays';
interface OfferSectionProps {
  selectedOffer: Offer['id'];
  onSelectOffer: (id: Offer['id']) => void;
}
const packMath: Record<Offer['id'], {
  price: number;
  units: number;
  compareAt?: number;
  save?: number;
}> = {
  // Multi-pack savings are measured against buying single Complete Systems at $49.95.
  single: {
    price: 49.95,
    units: 1
  },
  trio: {
    price: 99.9,
    units: 3,
    compareAt: 149.85,
    save: 49.95
  },
  crew: {
    price: 179.9,
    units: 6,
    compareAt: 299.7,
    save: 119.8
  }
};
export function OfferSection({
  selectedOffer,
  onSelectOffer
}: OfferSectionProps) {
  const [quantities, setQuantities] = useState<Record<Offer['id'], number>>({
    single: 1,
    trio: 1,
    crew: 1
  });
  // Optional +$19.95 lifetime upgrade on a single system. Never preselected.
  const [singleLifetime, setSingleLifetime] = useState(false);
  const upsell = useUpsellFlow();
  const sale = useActiveSale();
  // Cards reached by accepting a pack suggestion skip the quantity ladder (no chained upsells).
  const [reachedBySwitch, setReachedBySwitch] = useState<Partial<Record<Offer['id'], boolean>>>({});
  // "Want free shipping?" is customer-initiated and offered once per session.
  const [shippingOfferSeen, setShippingOfferSeen] = useState(() => flowSeen('single-lifetime-shipping'));
  const [singleShippingSeen, setSingleShippingSeen] = useState(() => flowSeen('single-no-lifetime'));
  const [missingCardLink, setMissingCardLink] = useState<Offer['id'] | null>(null);
  // RX Capability Inserts per card, chosen by quantity. Always starts at 0.
  const [rxQty, setRxQty] = useState<Record<Offer['id'], number>>({
    single: 0,
    trio: 0,
    crew: 0
  });
  const [careQty, setCareQty] = useState<Record<Offer['id'], number>>({
    single: 0,
    trio: 0,
    crew: 0
  });

  // Add-ons picked in the "What's in the box" build follow the customer to the $49.95 card.
  useEffect(() => onCarryToSingle(({
    rx,
    care,
    lifetime
  }) => {
    setQuantities(current => ({
      ...current,
      single: 1
    }));
    setRxQty(current => ({
      ...current,
      single: rx
    }));
    setCareQty(current => ({
      ...current,
      single: care
    }));
    setSingleLifetime(lifetime);
  }), []);
  const handleQuantity = (id: Offer['id'], next: number) => {
    const prev = quantities[id];
    if (next === prev) return;
    trackCustom(next > prev ? 'quantity_increased' : 'quantity_decreased', {
      pack: id,
      from: prev,
      to: next
    });
    // The lifetime add-on is a single-unit option; more units run the free-third flow instead.
    if (id === 'single' && next !== 1) setSingleLifetime(false);
    const suggestion = suggestionFor(id, next);
    if (suggestion) trackCustom('pack_switch_suggested', {
      from: id,
      to: suggestion.switchTo,
      quantity: next
    });
    setQuantities(current => ({
      ...current,
      [id]: next
    }));
  };
  const handleLifetime = (checked: boolean) => {
    setSingleLifetime(checked);
    trackCustom(checked ? 'lifetime_selected' : 'lifetime_declined', {
      stage: 'card'
    });
  };
  const switchPack = (from: Offer['id'], suggestion: PackSuggestion) => {
    const target = suggestion.switchTo;
    trackCustom('pack_switch_accepted', {
      from,
      to: target,
      quantity: quantities[from],
      target_quantity: suggestion.targetQuantity
    });
    setQuantities(current => target === 'business' ? {
      ...current,
      [from]: 1
    } : {
      ...current,
      [from]: 1,
      [target]: suggestion.targetQuantity
    });
    if (from === 'single') setSingleLifetime(false);
    const source: SelectionSource = target === 'trio' ? 'manual_quantity_three' : target === 'crew' ? 'manual_quantity_six' : 'manual_quantity_thirty';
    setSelectionSource(source);
    if (target === 'business') {
      // The business card takes the quantity and skips its ladder (reached by a switch).
      requestBusinessQuantity(suggestion.targetQuantity);
      document.getElementById('company')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      return;
    }
    setReachedBySwitch(current => ({
      ...current,
      [target]: true
    }));
    onSelectOffer(target);
    scrollToPackCta(target);
  };
  const openShippingOffer = () => {
    trackCustom('want_free_shipping_clicked', {
      total: SINGLE_WITH_LIFETIME_PRICE,
      lifetime: true
    });
    setShippingOfferSeen(true);
    onSelectOffer('single');
    upsell.start(buildFlow('single-lifetime-shipping', 'single_lifetime'));
  };

  // $49.95 single without lifetime: 3 for $99.90 with a $59.90 "only want 1?" option
  // on the same screen, then $57.90, then the standard $49.95. Once per session.
  const openSingleShippingOffer = () => {
    trackCustom('want_free_shipping_clicked', {
      total: SINGLE_PRICE,
      lifetime: false
    });
    setSingleShippingSeen(true);
    onSelectOffer('single');
    upsell.start(buildFlow('single-no-lifetime', 'single_standard'));
  };
  return <section id="offer" className="border-b border-white/10 bg-ink-900" data-id="element-916" data-mp="27u8gdg8hikj2">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-917" data-mp="1ttxqaff019yy">
        <h2 className="grit-headline text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-918" data-mp="1tw1qondovquh">
          Choose your <span className="text-grit-500" data-id="element-919" data-mp="iescaf9f3p01">loadout.</span>
        </h2>
        <p className="mt-4 text-lg font-medium text-steel-300 sm:text-xl" data-id="element-920" data-mp="idh84ec28f8e">
          Ready-made Complete Systems, all with free U.S. shipping.{' '}
          <a href="#build-your-x1" className="font-semibold text-grit-500 underline-offset-4 hover:underline" data-id="element-921" data-mp="ewbhzvwgspf6">
            Want only what you need? Build your own from $29.95 ↓
          </a>
        </p>

        <div className="mt-6 border-2 gold-edge bg-[#1a1408] p-5 sm:p-6" data-id="element-922" data-mp="ovak0vmq5mt7">
          <p className="flex flex-wrap items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#f2c94c]" data-id="element-923" data-mp="jvxw07k4a9p1">
            <InfinityIcon className="h-4 w-4" aria-hidden="true" data-id="element-924" data-mp="3scsr30uoapsk" />
            {warrantyCopy.value} · Free with every multi-pack
          </p>
          <p className="grit-headline mt-2 text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase gold-text" data-id="element-925" data-mp="3nalafxyweljw">
            {warrantyCopy.title}
          </p>
          <p className="mt-2 max-w-3xl text-[15px] font-semibold text-steel-200 sm:text-lg" data-id="element-926" data-mp="14ewiapaxdrr">
            {warrantyCopy.tagline} Break it, crack it, scratch a lens and we send a replacement. You never buy work
            glasses or a headlamp again. Included on Buy 2 Get 1 Free, Buy 4 Get 2 Free and the Crew Pack. A single
            system carries 1-year coverage, or add Lifetime for $19.95.{' '}
            <Link to="/legal/guarantee" className="text-[13px] font-medium text-steel-400 underline" data-id="element-927" data-mp="2o2zr5p15smti">
              Terms apply.
            </Link>
          </p>
        </div>

        <ul className="mt-10 flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:items-start" data-id="element-928" data-mp="2y0no8lm8m6eu">
          {offers.map(offer => {
          const isSelected = selectedOffer === offer.id;
          const qty = quantities[offer.id];
          const math = packMath[offer.id];
          const withLifetime = offer.id === 'single' && singleLifetime && qty === 1;
          const orderTotal = withLifetime ? SINGLE_WITH_LIFETIME_PRICE : math.price * qty;
          const orderUnits = math.units * qty;
          return <li key={offer.id} id={`pack-${offer.id}`} className="h-full scroll-mt-28" data-id="element-929" data-mp="3lqqcsrqy0u8p">
                <article className={`relative flex h-full flex-col border bg-ink-950 p-6 transition-colors duration-150 ease-grit lg:p-7 ${isSelected ? 'border-grit-500' : 'border-white/12 hover:border-white/30'} ${offer.featured ? 'lg:-mt-4 lg:pb-9' : ''}`} data-id="element-930" data-mp="1e3yoxlkk5g63">
                  <p className="-mx-6 -mt-6 mb-5 flex items-center justify-between gap-3 bg-grit-500 px-6 py-2.5 text-ink-950 lg:-mx-7 lg:-mt-7 lg:px-7" data-id="element-931" data-mp="104yxk65nun68">
                    <span className="font-display text-2xl font-extrabold uppercase leading-none" data-id="element-932" data-mp="1xtqwljzxnt6b">
                      {math.save ? `Save ${money(math.save).replace('.00', '')}` : 'Ready to work'}
                    </span>
                    <span className="text-right text-[10px] font-extrabold uppercase leading-tight tracking-[0.16em]" data-id="element-933" data-mp="1j3jo9x3n3qjc">
                      {sale.name}
                      {math.units > 1 && <span className="block" data-id="element-934" data-mp="2gk8q3qslassq">+ free units</span>}
                    </span>
                  </p>

                  <ReviewsStrip className="-mt-2 mb-2" data-id="element-935" data-mp="3vgkm274ofw91" />

                  {offer.image ? <>
                      {/* The pack artwork already carries the badge, the offer, the unit count and the per-X1 price. */}
                      <h3 className="sr-only" data-id="element-936" data-mp="9advnkdw8w00">
                        {[offer.eyebrow, offer.kicker, offer.name].filter(Boolean).join(' · ')}
                      </h3>
                      <img src={offer.image} alt={offer.imageAlt ?? offer.name} loading="lazy" width={1122} height={1402} className="h-auto w-full border border-white/10" data-id="element-937" data-mp="i9skkfeewyie" />
                    </> : <>
                      {offer.eyebrow && <p className={`mb-3 inline-flex self-start px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${offer.featured ? 'bg-grit-500 text-ink-950' : 'border border-grit-500/60 text-grit-500'}`} data-id="element-938" data-mp="10yphkr17254b">
                          {offer.eyebrow}
                        </p>}
                      {offer.kicker && <p className="text-xs font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-939" data-mp="1bkujxpavheok">{offer.kicker}</p>}
                      <h3 className={`mt-1 font-display font-extrabold uppercase leading-none text-offwhite ${offer.featured ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`} data-id="element-940" data-mp="wuwpwsq9hurg">
                        {offer.name}
                      </h3>
                      <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-941" data-mp="dxlu5i3u9jgr">{offer.units}</p>
                    </>}

                  <div className="mt-6 flex flex-wrap items-end gap-x-3 gap-y-1" data-id="element-942" data-mp="iz2qr1faijrj">
                    <span className={`font-display font-extrabold leading-none text-offwhite ${offer.featured ? 'text-6xl sm:text-7xl' : 'text-5xl'}`} data-id="element-943" data-mp="2knp4sdfw51et">
                      {money(orderTotal)}
                    </span>
                    {math.compareAt && !withLifetime && <span className="pb-1 text-base text-steel-500 line-through" data-id="element-944" data-mp="33k8dx2qp2pbd">
                        {money(math.compareAt * qty)}
                      </span>}
                  </div>

                  {withLifetime ? <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-[#ffe79a]" data-id="element-945" data-mp="2umejxygek7rd">
                      {money(math.price)} + {money(LIFETIME_UPGRADE_PRICE)} lifetime
                    </p> : math.save && <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-946" data-mp="1e8epv448ncc1">
                        You save {money(math.save * qty)}
                      </p>}
                  {offer.perUnit && !offer.image && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-947" data-mp="1dz9pwwdwl8hk">
                      {offer.perUnit}
                    </p>}
                  {qty > 1 && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-948" data-mp="1mv8ykl90hvwy">
                      {qty} packs · {orderUnits} X1 total
                    </p>}

                  {offer.highlight && !offer.image && <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none tracking-wide text-ink-950 sm:text-3xl" data-id="element-949" data-mp="21oucrqd6gpw4">
                      {offer.highlight}
                    </p>}
                  {offer.runtime && <p className="mt-3 font-display text-lg font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-950" data-mp="1xs6q0tethwjf">
                      {offer.runtime}
                    </p>}

                  <p className="mt-4 text-[13px] font-semibold uppercase tracking-wide text-steel-300" data-id="element-951" data-mp="26sv57rfy62xw">
                    {money(pricing.separateValue)} total value per X1
                  </p>

                  <div className="mt-5 border-t border-white/10 pt-5" data-id="element-952" data-mp="1e6acamjtpis8">
                    {/* Everyday items are pictured in the artwork's icon strip, so the list lives one tap away. */}
                    <details className="group" data-id="element-953" data-mp="1nvbriaijnw55">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite [&::-webkit-details-marker]:hidden" data-id="element-954" data-mp="1nx52g206x2iy">
                        Full list of what&apos;s in the box
                        <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-grit group-open:rotate-180" aria-hidden="true" data-id="element-955" data-mp="3239svveqwlv5" />
                      </summary>
                      <ul className="mt-3 grid gap-2 text-[14px] font-medium text-steel-200" data-id="element-956" data-mp="2t08njwxpeysj">
                        {offer.includes.filter(line => !/1-year|warranty|mystery gift/i.test(line)).map(line => <li key={line} className="flex items-start gap-2.5" data-id="element-957" data-mp="3praxwucptk5t">
                              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-958" data-mp="3btmapfv6e0xl" />
                              {line}
                            </li>)}
                      </ul>
                    </details>
                    <ul className="mt-4 grid gap-2 text-[15px] font-medium text-offwhite" data-id="element-959" data-mp="20g0krshhrvb0">
                      {offer.includes.filter(line => /1-year|warranty|mystery gift/i.test(line)).map(line => {
                    if (/1-year/i.test(line)) {
                      return <li key={line} className="mt-1 grid gap-2" data-id="element-960" data-mp="1cichljmys4vb">
                              <span className="flex items-start gap-2.5" data-id="element-961" data-mp="1cghwo5bp0cr8">
                                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-962" data-mp="17igcti6g09ep" />
                                {line}
                              </span>
                              {qty === 1 && <LifetimeUpgradeOption checked={singleLifetime} onChange={handleLifetime} showShippingOffer={false} onShippingOffer={openShippingOffer} data-id="element-963" data-mp="xn67akgu5jw6" />}
                              {!singleLifetime && <button type="button" onClick={() => {
                          onSelectOffer('trio');
                          scrollToPackCta('trio');
                        }} className="group flex items-center gap-3 border gold-edge bg-[#1a1408] p-3.5 text-left transition-colors duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-964" data-mp="2qi784hkx6sc5">
                                <InfinityIcon className="h-7 w-7 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-965" data-mp="2ciek4u0s2q7v" />
                                <span data-id="element-966" data-mp="2hjc338tm3oaf">
                                  <span className="block font-display text-base font-bold uppercase leading-tight text-[#ffe79a]" data-id="element-967" data-mp="2ljciavojc3ij">
                                    Want it covered for life?
                                  </span>
                                  <span className="mt-0.5 block text-[13px] font-semibold text-steel-300" data-id="element-968" data-mp="r0tkcdgxq4g">
                                    For just $49.95 more: 2 extra X1 plus the Lifetime Replacement Warranty on all
                                    3. Upgrade to Buy 2, Get 1 Free →
                                  </span>
                                </span>
                              </button>}
                            </li>;
                    }
                    if (/warranty/i.test(line)) {
                      return <li key={line} className="mt-1" data-id="element-969" data-mp="10nn0jo0gowtr">
                              <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-[background-color,box-shadow] duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-970" data-mp="1fhrx2ay6wh7h">
                                <GoldMedal size={86} data-id="element-971" data-mp="1rsix9zf0903s" />
                                <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-972" data-mp="1rv5buj0bcfvi">
                                  {math.units > 1 ? `All ${math.units} covered for life.` : 'Covered for life.'}
                                  <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-973" data-mp="1n1kcamj2ndu7">
                                    {warrantyCopy.title}. {warrantyCopy.tagline} {warrantyCopy.value},{' '}
                                    {warrantyCopy.status.toLowerCase()}.
                                  </span>
                                </p>
                              </Link>
                            </li>;
                    }
                    if (/mystery gift/i.test(line)) {
                      return <li key={line} className="mt-1 border border-grit-500/60 bg-grit-500/10 p-3.5 font-display text-lg font-bold uppercase leading-tight tracking-wide text-grit-500" data-id="element-974" data-mp="2yhr1qs9kznbd">
                              <span className="flex items-center gap-2" data-id="element-975" data-mp="3t1iq2fm2rsct">
                                <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-976" data-mp="3sz5yrbufg5n3" />
                                Free mystery gift
                              </span>
                              <span className="mt-1 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-977" data-mp="onag74dopyxi">
                                There&apos;s something else in the box. No clues. No extra charge. While supplies
                                last.
                              </span>
                            </li>;
                    }
                    return <li key={line} className="flex items-start gap-2.5" data-id="element-978" data-mp="35belo612bokr">
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-979" data-mp="359bl4s2aad1s" />
                            {line}
                          </li>;
                  })}
                    </ul>
                    {offer.splitNote && <div className="mt-4 border-l-2 border-grit-500 bg-white/[0.03] px-4 py-3" data-id="element-980" data-mp="36rseh0t40zpt">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-981" data-mp="31ua210fvw2d2">
                          {offer.splitNote.title}
                        </p>
                        <p className="mt-1 text-[14px] font-medium text-steel-200" data-id="element-982" data-mp="2964fq996m5b4">{offer.splitNote.body}</p>
                        <p className="mt-1 font-display text-base font-bold uppercase tracking-wide text-offwhite" data-id="element-983" data-mp="2nvae5v073rhy">
                          {offer.splitNote.each}
                        </p>
                      </div>}
                    <PrescriptionBadge className="mt-3" compact data-id="element-984" data-mp="1qebskvgsnebb" />
                    <ul className="mt-4 grid gap-1 border-t border-white/5 pt-4 text-sm text-steel-400" data-id="element-985" data-mp="1qe1zv04d6t1x">
                      {offer.supporting.map(line => <li key={line} data-id="element-986" data-mp="3vemhqsy1aw0g">{line}</li>)}
                    </ul>
                  </div>

                  <div className="mt-auto pt-7" data-id="element-987" data-mp="41idnqrfofjs">
                    <RxQuantityControl value={rxQty[offer.id]} max={orderUnits} onChange={next => {
                  trackCustom('multipack_rx_quantity', {
                    pack: offer.id,
                    quantity: next
                  });
                  setRxQty(current => ({
                    ...current,
                    [offer.id]: next
                  }));
                }} className="mb-3" data-id="element-988" data-mp="82c1yl8sfwd7" />
                    <CareKitControl value={careQty[offer.id]} max={orderUnits} onChange={next => {
                  trackCustom(next > careQty[offer.id] ? 'care_kit_added' : 'care_kit_removed', {
                    pack: offer.id,
                    quantity: next
                  });
                  setCareQty(current => ({
                    ...current,
                    [offer.id]: next
                  }));
                }} className="mb-4" data-id="element-989" data-mp="3lijka0e4nlju" />
                    <QuantityStepper value={qty} onChange={next => handleQuantity(offer.id, next)} label={`${offer.name} quantity`} className="mb-4" data-id="element-990" data-mp="x3rlwshwuo6q" />
                    {(() => {
                  const suggestion = suggestionFor(offer.id, qty);
                  if (!suggestion) return null;
                  return <div role="status" className="mb-4 border border-grit-500/70 bg-grit-500/10 p-3.5" data-id="element-991" data-mp="e68lgd9iqdvx">
                          <p className="text-[13px] font-semibold leading-snug text-steel-200" data-id="element-992" data-mp="2ck04uk404ks3">{suggestion.message}</p>
                          {!suggestion.replacesCheckout && <button type="button" onClick={() => switchPack(offer.id, suggestion)} className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-grit-500 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-993" data-mp="2fn69zrjssvuz">
                              {suggestion.action}
                              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-994" data-mp="21mvkaru85nbe" />
                            </button>}
                        </div>;
                })()}
                    {(() => {
                  const suggestion = suggestionFor(offer.id, qty);
                  if (suggestion?.replacesCheckout) {
                    // The card's own checkout would charge more for the same units: the switch replaces it.
                    return <button id={`pack-${offer.id}-cta`} type="button" onClick={() => switchPack(offer.id, suggestion)} className="flex w-full scroll-mb-28 items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-995" data-mp="zalzi0gck0sk">
                            {suggestion.action}
                            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-996" data-mp="1mzeo1bun36d5" />
                          </button>;
                  }
                  const ctaClass = `flex w-full scroll-mb-28 items-center justify-center gap-2 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${offer.featured ? 'bg-grit-500 text-ink-950 hover:bg-grit-400 text-2xl' : 'border-2 border-grit-500 text-grit-500 hover:bg-grit-500 hover:text-ink-950 text-xl'}`;
                  // The button price follows the card: RX inserts and Care Kits chosen here are added in.
                  const rxCount = Math.min(rxQty[offer.id], orderUnits);
                  const careCount = Math.min(careQty[offer.id], orderUnits);
                  const addonTotal = Math.round((rxCount * builderPrices.rxInsert + careCount * builderPrices.careKit) * 100) / 100;
                  const ctaTotal = Math.round((orderTotal + addonTotal) * 100) / 100;
                  const ctaLabel = withPrice(withLifetime ? `Get the X1 + Lifetime — ${money(orderTotal)}` : qty > 1 ? `Get ${orderUnits} X1 for ${money(orderTotal)}` : offer.cta, ctaTotal);
                  // Explains a button total that differs from the pack price, so it never reads as a random number.
                  const ctaBreakdown = addonTotal > 0 ? [`${withLifetime ? 'X1 + Lifetime' : `${orderUnits} X1`} ${money(orderTotal)}`, rxCount > 0 ? `${rxCount} RX insert${rxCount > 1 ? 's' : ''} ${money(rxCount * builderPrices.rxInsert)}` : null, careCount > 0 ? `${careCount} Care Kit${careCount > 1 ? 's' : ''} ${money(careCount * builderPrices.careKit)}` : null].filter(Boolean).join(' + ') : null;
                  // 1 X1 + Lifetime is its own Shopify product ($69.90, shipping at checkout).
                  const linkKey = withLifetime ? 'singleX1LifetimeNormal6990' : offer.link;
                  const href = cartLink(linkKey, withLifetime ? 1 : qty);
                  return <>
                        <a id={`pack-${offer.id}-cta`} href={href || '#offer'} data-buy-intent="true" onClick={event => {
                      // RX inserts chosen on this card ride along with whatever this checkout ends on.
                      setPendingRx(Math.min(rxQty[offer.id], orderUnits));
                      setPendingCare(Math.min(careQty[offer.id], orderUnits));
                      onSelectOffer(offer.id);
                      trackCustom('product_selected', {
                        pack: offer.id,
                        quantity: qty,
                        lifetime: withLifetime
                      });
                      if (offer.id === 'single' && qty === 1 && !withLifetime) {
                        trackCustom('lifetime_declined', {
                          stage: 'checkout'
                        });
                      }
                      // At most one negotiation flow per checkout, each flow once per session.
                      const next = checkoutFlow(offer.id, qty, {
                        lifetime: withLifetime,
                        reachedBySwitch: reachedBySwitch[offer.id]
                      });
                      if (next && !flowSeen(next.kind)) {
                        event.preventDefault();
                        if (next.kind === 'single-no-lifetime') setSingleShippingSeen(true);
                        if (next.kind === 'single-lifetime-shipping') setShippingOfferSeen(true);
                        upsell.start(buildFlow(next.kind, next.source));
                        return;
                      }
                      setSelectionSource(offer.id === 'single' ? withLifetime ? 'single_lifetime' : qty === 2 ? 'two_unit_selection' : 'single_standard' : offer.id === 'trio' ? reachedBySwitch.trio ? 'manual_quantity_three' : 'direct_three_pack' : reachedBySwitch.crew ? 'manual_quantity_six' : 'direct_six_pack');
                      if (!href) {
                        event.preventDefault();
                        setMissingCardLink(offer.id);
                        console.warn(`[GritVue] PRODUCT_LINKS.${linkKey} is empty.`);
                        return;
                      }
                      trackCustom(packEvents[offer.id].addToCart, {
                        quantity: qty,
                        value: orderTotal
                      });
                      trackCustom('upsell_flow_completed', {
                        final_product: linkKey,
                        value: orderTotal,
                        quantity: qty,
                        result: 'direct'
                      });
                      trackShopifyAddToCart(event, {
                        content_name: withLifetime ? 'GritVue X1 Complete System + Lifetime Replacement Warranty' : offer.name,
                        content_ids: [variantIdOf(PRODUCT_LINKS[linkKey])],
                        quantity: qty,
                        value: Number(orderTotal.toFixed(2))
                      });
                    }} className={ctaClass} data-id="element-997" data-mp="15rfddgvmt3wh">
                          {ctaLabel}
                        </a>
                        {ctaBreakdown && <p className="mt-2 text-center text-[12px] font-semibold text-steel-300" data-id="element-998" data-mp="10ph1v7gy9w1j">
                            Includes: {ctaBreakdown}
                          </p>}
                        </>;
                })()}
                    {missingCardLink === offer.id && <p role="alert" className="mt-3 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-999" data-mp="14q0zsd9cl3oq">
                        This checkout link isn&apos;t connected yet. Please try again shortly or email
                        support@gritvuegear.com.
                      </p>}
                    <UspsBadge className="mt-3" note={offer.note} data-id="element-1000" data-mp="1ofl4bbiejpqy" />
                    <SecureCheckoutNote className="mt-4" data-id="element-1001" data-mp="3u7rk9tcprhyh" />
                    {offer.badge && <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-grit-500" data-id="element-1002" data-mp="3q7pscbws62we">
                        {offer.badge}
                      </p>}
                    {offer.footnote && <p className="mt-3 text-[13px] font-medium leading-relaxed text-steel-200" data-id="element-1003" data-mp="3k432chgb0ao4">{offer.footnote}</p>}
                  </div>
                </article>
              </li>;
        })}
        </ul>

        <CompanyPack data-id="element-1004" data-mp="2wf0ayhvvb3gf" />

        <IncludedGear data-id="element-1005" data-mp="2weqtxjssw6mv" />

        <UpsellModal step={upsell.step} confirmation={upsell.confirmation} onContinue={upsell.continueToCheckout} onFollowUp={upsell.followUp} routing={upsell.routing} missingLink={upsell.missingLink} onAccept={upsell.accept} onAcceptAlternate={upsell.acceptAlternate} onDecline={upsell.decline} onClose={upsell.close} data-id="element-1006" data-mp="2we8mdr0ou5zn" />

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-1007" data-mp="lkw7gtm4hua5">
          <div data-id="element-1008" data-mp="2mpq7li8zgc53">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1009" data-mp="2mpgcxhcztgt2">Secure checkout</p>
            <div className="mt-2" data-id="element-1010" data-mp="2hr53h7qm18bd">
              <PaymentLogos data-id="element-1011" data-mp="2ho0rvpatfaao" />
            </div>
          </div>
          <p className="max-w-md text-[13px] text-steel-400" data-id="element-1012" data-mp="2cpz8f39h98yo">
            30-day returns · Free U.S. shipping on every order · Taxes, if
            applicable, calculated at checkout ·{' '}
            <Link to="/legal/shipping" className="font-semibold text-grit-500 hover:underline" data-id="element-1013" data-mp="23rdwhqyigs6v">
              Shipping &amp; returns
            </Link>
          </p>
        </div>
      </div>
    </section>;
}